import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import constants from '@web/config/constants';
import { useMatch } from 'react-router-dom';
import { DmsHeader } from './dms-header';
import { DMSReportSection } from './dms-report-section';
import { DMSAsideContent } from './dms-aside-content';
import { DmsState, useDmsState } from '@packages/contexts/dms';
import React from 'react';

interface RouteLayoutConfig {
    patterns: string[];
    header?: React.ComponentType;
    reports?: React.ComponentType;
    aside?: React.ComponentType;
    resolveRowGuid?: (ctx: { dms: DmsState }) => string | undefined | null;
}

const routeLayoutConfigs: RouteLayoutConfig[] = [
    {
        patterns: ['/vin/:vin/repair-orders/:repairOrder', '/vin/:vin/repair-orders'],
        header: DmsHeader,
        reports: DMSReportSection,
        aside: DMSAsideContent,
        resolveRowGuid: ({ dms }) => dms.selectedRepairOrder?.roNumber,
    },
    // Future modules add here
];

interface LayoutStyleProps {
    hideAsideContent: boolean;
    hideHeader: boolean;
    hideReportsSection: boolean;
}

const useLayoutStyles = createUseStyles({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    header: ({ hideHeader }: LayoutStyleProps) => ({
        alignItems: 'center',
        borderBottom: `1px solid ${colors.grayThree}`,
        display: hideHeader ? 'none' : 'flex',
        flexShrink: 0,
        height: constants.bmisHeaderHeight,
        justifyContent: 'space-between',
        padding: '0 24px',
    }),
    contentWrapper: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        overflow: 'hidden',
    },
    asideContent: ({ hideAsideContent }: LayoutStyleProps) => ({
        backgroundColor: colors.grayOne,
        borderLeft: `1px solid ${colors.grayThree}`,
        flexBasis: hideAsideContent ? 0 : 400,
        flexShrink: 0,
        overflowY: hideAsideContent ? 'hidden' : 'auto',
    }),
    details: {
        backgroundColor: colors.white,
        flexGrow: 1,
        overflowY: 'auto',
        overflow: 'hidden',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    reportSections: ({ hideReportsSection }: LayoutStyleProps) => ({
        backgroundColor: colors.grayOne,
        flexBasis: hideReportsSection ? 0 : 248,
        flexShrink: 0,
        overflowY: 'auto',
    }),
    '@media only screen and (max-width: 1100px)': {
        reportSections: {
            display: 'none',
        },
    },
});

interface LayoutProps {
    children?: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
    const dmsState = useDmsState();
    const ctxBundle = { dms: dmsState };

    let activeConfig: RouteLayoutConfig | undefined;
    for (const cfg of routeLayoutConfigs) {
        const matched = cfg.patterns.some((p) => !!useMatch(p));
        if (matched) {
            activeConfig = cfg;
            break;
        }
    }

    const rowGuid = activeConfig?.resolveRowGuid?.(ctxBundle);
    const HeaderComp = activeConfig?.header;
    const ReportsComp = activeConfig?.reports;
    const AsideComp = activeConfig?.aside;

    const classes = useLayoutStyles({
        hideAsideContent: !AsideComp || !rowGuid,
        hideHeader: !HeaderComp || !rowGuid,
        hideReportsSection: !ReportsComp || !rowGuid,
    });

    return (
        <div className={classes.wrapper}>
            <div className={classes.header}>{HeaderComp && <HeaderComp />}</div>
            <div className={classes.contentWrapper}>
                <div className={classes.reportSections}>{ReportsComp && <ReportsComp />}</div>
                <div className={classes.details}>{children}</div>
                <div className={classes.asideContent}>{AsideComp && <AsideComp />}</div>
            </div>
        </div>
    );
};
